import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Site } from 'four-nets/site';

import FieldPriceView from 'four-nets/bazaar/filter/field-price-view';
import FilterView from 'four-nets/bazaar/filter/filter-view';
import FieldView from 'four-nets/bazaar/filter/field-view';

import { CategoriesFiltersQuery } from './categories-filters.graphql';
import priceFilterStyles from './filter-view.scss';

type Counties = CategoriesFiltersQuery['counties'];

export interface Props {
    counties: Counties;
    initialCountiesValue?: string | string[];
    initialPriceValue?: string;
    confirmCounty: (countyIds: string[]) => void;
    confirmPrice: (prices: string[]) => void;
    clearCounty: () => void;
    clearPrice: () => void;
}

interface State {
    selectedCountyFields: Set<string>;
    pendingCountyFields: Set<string>;
    fromPrice?: string;
    toPrice?: string;
    fulltextValue: string;
}

class CategoriesFilters extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    constructor(props: Props) {
        super(props);

        const selectedCountyFields = new Set<string>();
        if (props.initialCountiesValue) {
            if (typeof props.initialCountiesValue !== 'object') {
                selectedCountyFields.add(props.initialCountiesValue);
            } else {
                props.initialCountiesValue.forEach(name => {
                    selectedCountyFields.add(name);
                });
            }
        }

        let fromPrice;
        let toPrice;
        if (props.initialPriceValue) {
            [fromPrice, toPrice] = this.fromValue(props.initialPriceValue);
        }

        this.state = {
            selectedCountyFields,
            pendingCountyFields: new Set(),
            fromPrice,
            toPrice,
            fulltextValue: '',
        };
    }

    toggleCountyField = (name: string) => () => {
        const selectedCountyFields = this.state.selectedCountyFields;
        const pendingCountyFields = this.state.pendingCountyFields;

        if (selectedCountyFields.has(name)) {
            selectedCountyFields.delete(name);
            pendingCountyFields.delete(name);
        } else if (pendingCountyFields.has(name)) {
            pendingCountyFields.delete(name);
        } else {
            pendingCountyFields.add(name);
        }

        this.setState({
            selectedCountyFields: new Set(selectedCountyFields),
            pendingCountyFields: new Set(pendingCountyFields),
        });
    };

    clearCountyFields = () => {
        this.setState({
            selectedCountyFields: new Set(),
            pendingCountyFields: new Set(),
        });
    };

    toName = () => {
        const { site } = this.context;

        if (!this.state.fromPrice && !this.state.toPrice) {
            return '';
        }
        if (this.state.toPrice === null || this.state.toPrice === undefined) {
            return `${this.state.fromPrice || 0} ${site && site.currency} - `;
        }
        return `${this.state.fromPrice || 0} ${site && site.currency} - ${this.state.toPrice} ${site && site.currency}`;
    };

    toValue = () => {
        if (!this.state.fromPrice && !this.state.toPrice) {
            return '';
        }
        if (!this.state.toPrice) {
            return `${this.state.fromPrice || 0}`;
        }
        return `${this.state.fromPrice || 0}-${this.state.toPrice}`;
    };

    fromValue = (value: any) => {
        let fromPrice;
        let toPrice;
        if (value) {
            [fromPrice, toPrice] = String(value).split('-');
        }

        return [fromPrice, toPrice];
    };

    setFrom = (event: KeyboardEvent) => {
        this.setState({
            fromPrice: (event.target as HTMLInputElement).value,
        });
    };

    setTo = (event: KeyboardEvent) => {
        this.setState({
            toPrice: (event.target as HTMLInputElement).value,
        });
    };

    clearPriceField = () => {
        this.setState({
            fromPrice: undefined,
            toPrice: undefined,
        });
    };

    isMatchingSearch = (name?: string) => {
        if (!name) {
            return true;
        }

        return name.toLowerCase().indexOf(this.state.fulltextValue.toLowerCase()) !== -1;
    };

    onFulltextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            fulltextValue: (event.target as HTMLInputElement).value,
        });
    };

    noValuesInFilter(counties: Counties) {
        const { selectedCountyFields, pendingCountyFields } = this.state;

        const isFiltering = this.state.fulltextValue !== '';
        const filteredFields = counties?.edges
            ? counties.edges.filter(
                  edge =>
                      !(selectedCountyFields.has(edge!.node!.name) && pendingCountyFields.has(edge!.node!.name)) &&
                      this.isMatchingSearch(edge!.node!.name)
              )
            : [];

        return isFiltering && filteredFields.length === 0;
    }

    activeFilterFields(counties: Counties) {
        const { selectedCountyFields, pendingCountyFields } = this.state;
        const activeCounties = counties?.edges.length
            ? counties.edges.filter(
                  edge => selectedCountyFields.has(edge!.node!.name) || pendingCountyFields.has(edge!.node!.name)
              )
            : [];
        return activeCounties.map(edge => edge!.node!.name).join(', ');
    }

    countyFieldViews(counties: Counties) {
        const selectedFields: JSX.Element[] = [];
        const availableFields: JSX.Element[] = [];

        counties?.edges &&
            counties.edges.forEach(edge => {
                const county = edge?.node!;

                if (this.state.selectedCountyFields.has(county.name)) {
                    selectedFields.push(
                        <FieldView
                            key={county.id}
                            name={county.name}
                            selected={this.state.selectedCountyFields.has(county.name)}
                            pending={this.state.pendingCountyFields.has(county.name)}
                            onClick={this.toggleCountyField(county.name)}
                        />
                    );
                } else if (this.state.pendingCountyFields.has(county.name) || this.isMatchingSearch(county.name)) {
                    availableFields.push(
                        <FieldView
                            key={county.id}
                            name={county.name}
                            selected={this.state.selectedCountyFields.has(county.name)}
                            pending={this.state.pendingCountyFields.has(county.name)}
                            onClick={this.toggleCountyField(county.name)}
                        />
                    );
                }
            });

        return [...selectedFields, ...availableFields];
    }

    priceFieldViews() {
        return [
            <FieldPriceView
                key='price'
                from={this.state.fromPrice}
                to={this.state.toPrice}
                setFrom={this.setFrom}
                setTo={this.setTo}
            />,
        ];
    }

    clearCounty = () => {
        this.clearCountyFields();
        this.props.clearCounty();
    };

    clearPrice = () => {
        this.clearPriceField();
        this.props.clearPrice();
    };

    confirmCounty = () => {
        this.setState(
            {
                selectedCountyFields: new Set([
                    ...Array.from(this.state.selectedCountyFields),
                    ...Array.from(this.state.pendingCountyFields),
                ]),
                pendingCountyFields: new Set(),
            },
            () => this.props.confirmCounty(Array.from(this.state.selectedCountyFields))
        );
    };

    confirmPrice = () => {
        if (this.toValue()) {
            this.props.confirmPrice([this.toValue()]);
        } else {
            this.clearPrice();
        }
    };

    render() {
        return (
            <React.Fragment>
                <FilterView
                    title={<FormattedMessage id='BAZAAR_FILTER_COUNTY_TITLE' />}
                    fields={this.countyFieldViews(this.props.counties)}
                    activeFilterFields={this.activeFilterFields(this.props.counties)}
                    fulltextValue={this.state.fulltextValue}
                    onFulltextChange={this.onFulltextChange}
                    displayText={this.noValuesInFilter(this.props.counties)}
                    confirm={this.confirmCounty}
                    clear={this.clearCounty}
                />
                <FilterView
                    style={{ container: priceFilterStyles.container }}
                    title={<FormattedMessage id='BAZAAR_FILTER_PRICE_TITLE' />}
                    hideSearch={true}
                    fields={this.priceFieldViews()}
                    activeFilterFields={this.toValue() ? this.toName() : ''}
                    clear={this.clearPrice}
                    confirm={this.confirmPrice}
                />
            </React.Fragment>
        );
    }
}

export default CategoriesFilters;
